import { ethers } from 'ethers';

// Polymarket contract addresses (Polygon mainnet)
const POLYMARKET_CONTRACTS = {
  CTF_EXCHANGE: '0x4bFb41d5B3570DeFd03C39a9A4D8dE6Bd8B8982A',
  NEG_RISK_ADAPTER: '0xd91E80cF2E7be2e162c6513ceD06f1dD0dA35296',
  USDC: '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174',
};

// ERC20 ABI for USDC
const ERC20_ABI = [
  'function approve(address spender, uint256 amount) external returns (bool)',
  'function allowance(address owner, address spender) external view returns (uint256)',
  'function balanceOf(address account) external view returns (uint256)',
  'function decimals() external view returns (uint8)',
];

// CTF Exchange ABI (simplified)
const CTF_EXCHANGE_ABI = [
  'function fillOrder(address maker, uint256 makerAssetId, uint256 takerAssetId, uint256 makerAmount, uint256 takerAmount, bytes calldata makerData, bytes calldata takerData) external payable',
  'function orderCanceled(bytes32 orderHash) external view returns (bool)',
];

export interface ConnectedWallet {
  address: string;
  provider: ethers.BrowserProvider;
  signer: ethers.JsonRpcSigner;
  chainId: number;
  balance: string;
}

class WalletService {
  private provider: ethers.BrowserProvider | null = null;
  private signer: ethers.JsonRpcSigner | null = null;
  private address: string | null = null;

  async connect(): Promise<ConnectedWallet | null> {
    try {
      // Check if MetaMask or other Web3 provider is available
      if (!window.ethereum) {
        throw new Error('No Web3 provider found. Please install MetaMask.');
      }

      // Request account access
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts'
      });

      if (!accounts || accounts.length === 0) {
        throw new Error('No accounts found');
      }

      this.address = accounts[0];
      this.provider = new ethers.BrowserProvider(window.ethereum);
      this.signer = await this.provider.getSigner();

      const network = await this.provider.getNetwork();
      const chainId = Number(network.chainId);

      // Check if on Polygon
      if (chainId !== 137) {
        // Try to switch to Polygon
        try {
          await window.ethereum.request({
            method: 'wallet_switchEthereumChain',
            params: [{ chainId: '0x89' }], // 137 in hex
          });
        } catch (switchError: any) {
          // If Polygon not added, add it
          if (switchError.code === 4902) {
            await window.ethereum.request({
              method: 'wallet_addEthereumChain',
              params: [{
                chainId: '0x89',
                chainName: 'Polygon Mainnet',
                nativeCurrency: {
                  name: 'MATIC',
                  symbol: 'MATIC',
                  decimals: 18
                },
                rpcUrls: ['https://polygon-rpc.com'],
                blockExplorerUrls: ['https://polygonscan.com']
              }]
            });
          }
        }
      }

      // Get USDC balance
      const usdcContract = new ethers.Contract(
        POLYMARKET_CONTRACTS.USDC,
        ERC20_ABI,
        this.provider
      );

      const balance = await usdcContract.balanceOf(this.address);
      const decimals = await usdcContract.decimals();

      return {
        address: this.address,
        provider: this.provider,
        signer: this.signer,
        chainId,
        balance: ethers.formatUnits(balance, decimals)
      };
    } catch (error: any) {
      console.error('Wallet connection error:', error);
      throw new Error(error.message || 'Failed to connect wallet');
    }
  }

  async disconnect(): Promise<void> {
    this.provider = null;
    this.signer = null;
    this.address = null;
  }

  async executeTrade(
    marketId: string,
    side: 'YES' | 'NO',
    amount: number,
    maxPrice: number
  ): Promise<{ hash: string; status: string }> {
    if (!this.signer || !this.address) {
      throw new Error('Wallet not connected');
    }

    try {
      // This is a simplified example - real Polymarket trading requires
      // integration with their CLOB API for order matching
      
      // 1. Check USDC allowance
      const usdcContract = new ethers.Contract(
        POLYMARKET_CONTRACTS.USDC,
        ERC20_ABI,
        this.signer
      );

      const allowance = await usdcContract.allowance(
        this.address,
        POLYMARKET_CONTRACTS.CTF_EXCHANGE
      );

      const amountWei = ethers.parseUnits(amount.toString(), 6); // USDC has 6 decimals

      // 2. Approve if needed
      if (allowance < amountWei) {
        console.log('Approving USDC spend...');
        const approveTx = await usdcContract.approve(
          POLYMARKET_CONTRACTS.CTF_EXCHANGE,
          ethers.MaxUint256
        );
        await approveTx.wait();
        console.log('USDC approved');
      }

      // 3. Execute trade (simplified - real implementation needs CLOB integration)
      // In production, you would:
      // - Get order book from Polymarket CLOB
      // - Create and sign order
      // - Submit to Polymarket API
      
      // Placeholder for actual trade execution
      console.log(`Executing trade: ${side} $${amount} at max ${maxPrice}`);

      // Return mock result - replace with actual transaction hash
      return {
        hash: '0x...',
        status: 'pending'
      };
    } catch (error: any) {
      console.error('Trade execution error:', error);
      throw new Error(error.message || 'Failed to execute trade');
    }
  }

  async getBalance(): Promise<string> {
    if (!this.provider || !this.address) {
      throw new Error('Wallet not connected');
    }

    const usdcContract = new ethers.Contract(
      POLYMARKET_CONTRACTS.USDC,
      ERC20_ABI,
      this.provider
    );

    const balance = await usdcContract.balanceOf(this.address);
    const decimals = await usdcContract.decimals();

    return ethers.formatUnits(balance, decimals);
  }

  // Listen for account changes
  onAccountsChanged(callback: (accounts: string[]) => void): void {
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', callback);
    }
  }

  // Listen for chain changes
  onChainChanged(callback: (chainId: string) => void): void {
    if (window.ethereum) {
      window.ethereum.on('chainChanged', callback);
    }
  }

  // Remove listeners
  removeListeners(): void {
    if (window.ethereum) {
      window.ethereum.removeAllListeners('accountsChanged');
      window.ethereum.removeAllListeners('chainChanged');
    }
  }
}

// TypeScript declaration for window.ethereum
declare global {
  interface Window {
    ethereum?: any;
  }
}

export const walletService = new WalletService();
